import { axiosInstance } from '@/api/mutator/custom-instance';
import { isStandingInvoice } from '@/types/billing';

export type ReportType = 'weekly' | 'daily' | 'monthly';

/** Query values a report accepts; `includeChecklists` is the daily plan's work-sheet toggle. */
export type ReportParams = Record<string, string | number | boolean>;

const DEFAULT_FILENAMES: Record<ReportType, (params: ReportParams) => string> = {
  weekly: (p) => `Wochenplan_KW${p.week}_${p.year}.pdf`,
  daily: (p) => `Tagesplan_${p.date}.pdf`,
  monthly: (p) => `Zeitbestaetigung_${p.year}-${p.month}.pdf`,
};

/**
 * Downloads a PDF report using the existing axios instance (auth cookie + X-Tenant-Id).
 * If employeeId is provided, uses admin endpoint; otherwise self-service /me/ path.
 */
export async function downloadReport(
  type: ReportType,
  params: ReportParams,
  employeeId?: string,
): Promise<void> {
  const basePath = employeeId
    ? `/api/v1/employees/${employeeId}/reports`
    : '/api/v1/me/reports';

  const response = await axiosInstance.get(`${basePath}/${type}`, {
    params,
    responseType: 'blob',
  });

  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);

  // Extract filename from Content-Disposition header, fallback to spec-defined names
  const disposition = response.headers['content-disposition'] as string | undefined;
  const filename = disposition?.match(/filename="(.+)"/)?.[1]
    ?? DEFAULT_FILENAMES[type](params);

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** The day's checklist protocol as PDF (Verwaltung), optionally narrowed by employee/object. */
export async function downloadChecklistProtocolPdf(
  date: string,
  employeeId?: string,
  objectId?: string,
): Promise<void> {
  const response = await axiosInstance.get('/api/v1/checklist-protocol/pdf', {
    params: { date, employeeId: employeeId || undefined, objectId: objectId || undefined },
    responseType: 'blob',
  });

  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);

  const disposition = response.headers['content-disposition'] as string | undefined;
  const filename = disposition?.match(/filename="(.+)"/)?.[1]
    ?? `Checklisten-Protokoll_${date}.pdf`;

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export async function downloadOfferPdf(
  offerId: string,
): Promise<void> {
  const response = await axiosInstance.get(
    `/api/v1/offers/${offerId}/pdf`,
    { responseType: 'blob' },
  );

  const blob = new Blob([response.data], {
    type: 'application/pdf',
  });
  const url = URL.createObjectURL(blob);

  const disposition = response.headers[
    'content-disposition'
  ] as string | undefined;
  const filename = disposition?.match(/filename="(.+)"/)?.[1]
    ?? `Angebot_${offerId}.pdf`;

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export async function downloadInvoicePdf(
  invoiceId: string,
  invoiceNumber?: string | null,
  billingInterval?: string | null,
): Promise<void> {
  const response = await axiosInstance.get(
    `/api/v1/invoices/${invoiceId}/pdf`,
    { responseType: 'blob' },
  );

  const blob = new Blob([response.data], {
    type: 'application/pdf',
  });
  const url = URL.createObjectURL(blob);

  const disposition = response.headers[
    'content-disposition'
  ] as string | undefined;
  // why: a yearly invoice is a „Dauerrechnung“ (FACF-106) — the fallback name mirrors the PDF title.
  const prefix = isStandingInvoice(billingInterval) ? 'Dauerrechnung' : 'Rechnung';
  const filename = disposition?.match(/filename="(.+)"/)?.[1]
    ?? `${prefix}_${invoiceNumber || invoiceId}.pdf`;

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export async function downloadTenantInvoicePdf(
  invoiceId: string,
  invoiceNumber?: string | null,
): Promise<void> {
  const response = await axiosInstance.get(
    `/api/v1/tenant-billing/invoices/${invoiceId}/pdf`,
    { responseType: 'blob' },
  );

  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);

  const disposition = response.headers[
    'content-disposition'
  ] as string | undefined;
  const filename = disposition?.match(/filename="(.+)"/)?.[1]
    ?? `SaaS-Rechnung_${invoiceNumber || invoiceId}.pdf`;

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// Tenant-portal variant: hits the auth-scoped /me/ route so a Verwaltungs-User
// can only ever download their own SaaS invoices, never another tenant's.
export async function downloadMyTenantInvoicePdf(
  invoiceId: string,
  invoiceNumber?: string | null,
): Promise<void> {
  const response = await axiosInstance.get(
    `/api/v1/me/tenant-billing/invoices/${invoiceId}/pdf`,
    { responseType: 'blob' },
  );

  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);

  const disposition = response.headers[
    'content-disposition'
  ] as string | undefined;
  const filename = disposition?.match(/filename="(.+)"/)?.[1]
    ?? `SaaS-Rechnung_${invoiceNumber || invoiceId}.pdf`;

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
